/**
 * AI Agents roster + orchestration types — real LMS agent identities (Vapi assistant IDs for
 * voice/inbound; Make scenarios for text). Orchestration state is LOCAL only (no backend
 * dispatch); the live call-event feed that would drive floor/stats/outcomes does not exist yet.
 */

export type AutonomyMode = 'auto' | 'approve' | 'shadow' | 'paused';
/** voice = outbound dialer; inbound = receives only; text = Make scenario (not dialable). */
export type AgentKind = 'voice' | 'inbound' | 'text';
export type LaunchMode = 'approval' | 'oneclick' | 'passive';

export interface AiAgent {
	key: string; // stable id
	name: string; // display name
	role: string; // function / subtitle
	kind: AgentKind;
	launch: LaunchMode;
	/** Dial queue this agent staffs; null for text (event-driven) agents. */
	queueId: string | null;
	/** Real Vapi assistant id; null for text agents (Make scenarios, no Vapi assistant). Not shown to end users — internal reference only. */
	vapiId: string | null;
	/** Single emoji shown before the agent's name on its card. */
	icon: string;
	/** Plain-English blurb: what it does, what it's for, what's expected of it. Shown on the card in place of the raw role/id. */
	description: string;
	note?: string;
}

export interface WorkQueue {
	id: string;
	name: string;
	defaultAgentKey: string | null;
	note?: string;
	approvalGated?: boolean; // cold/intro requires the approval step
	passive?: boolean; // inbound — receives, not dispatched
	planned?: boolean; // no agent yet
}

/** Assignment of an agent to a queue, optionally scoped to campaigns ([] = all campaigns). */
export interface Assignment {
	agentKey: string | null;
	campaignIds: number[];
}

export const AUTONOMY_MODES: AutonomyMode[] = ['auto', 'approve', 'shadow', 'paused'];

/**
 * VOICE dialers — real Vapi roster. Ivy opens cold (approval-gated); the rest are warm
 * one-click follow-ups. These map 1:1 to the dial queues below.
 */
export const VOICE_AGENTS: AiAgent[] = [
	{
		key: 'ivy',
		name: 'Ivy',
		role: 'Intro / warm-up',
		kind: 'voice',
		launch: 'approval',
		queueId: 'intro',
		vapiId: 'de458bb3-e346-42e5-b2b7-ddd63646883e', // temp: test-account assistant, swap back once the real Ivy is provisioned
		icon: '👋',
		description: "Opens brand-new leads with a cold introduction call. Every dial needs human approval first — she's expected to warm the prospect up and hand off a qualified lead to the queue.",
	},
	{
		key: 'warren',
		name: 'Warren',
		role: 'Pre-call warmer',
		kind: 'voice',
		launch: 'oneclick',
		queueId: 'precall',
		vapiId: 'f7d3c11f-94c6-4b94-b6c4-c1e8fb8e48d6',
		icon: '🔥',
		description: 'Calls prospects ahead of their scheduled appointment to build rapport and confirm interest. Launches with one click — expected to lift show-up rates by re-engaging before the meeting.',
	},
	{
		key: 'connor',
		name: 'Connor',
		role: 'Appointment confirmation',
		kind: 'voice',
		launch: 'oneclick',
		queueId: 'confirm',
		vapiId: '82093604-0bfe-4c8d-8006-0d838f5218c1',
		icon: '📅',
		description: 'Confirms upcoming appointments with prospects over the phone. Expected to reduce no-shows by verifying date, time, and answering last-minute questions.',
	},
	{
		key: 'sam',
		name: 'Sam',
		role: 'Same-day confirmation',
		kind: 'voice',
		launch: 'oneclick',
		queueId: 'sameday',
		vapiId: '2a1c3a36-dc6c-4b64-8202-b510d7f1806d',
		icon: '⏰',
		description: 'Confirms same-day PHONE/ZOOM appointments only (FACE appointments are confirmed ahead of time since the closer has to travel). Expected to catch late cancellations before the closer heads out.',
	},
	{
		key: 'renee',
		name: 'Renée',
		role: 'Reschedule recovery',
		kind: 'voice',
		launch: 'oneclick',
		queueId: 'reschedule',
		vapiId: 'a43e2b90-92c8-4968-8e27-7892d2c89285',
		icon: '🔄',
		description: 'Reaches out to prospects who missed or canceled their appointment to get them rebooked. Expected to recover opportunities that would otherwise be lost.',
	},
	{
		key: 'parker',
		name: 'Parker',
		role: 'Post-meeting outcome',
		kind: 'voice',
		launch: 'oneclick',
		queueId: 'postmeeting',
		vapiId: '01b25037-d1ad-4601-9713-c1d25cdda230',
		icon: '📝',
		description: 'Follows up right after a completed meeting to capture the outcome. Expected to log next steps and keep the deal moving through the pipeline.',
	},
];

/** INBOUND — receives calls, never dispatched outbound; no dial queue of its own to launch. */
export const INBOUND_AGENTS: AiAgent[] = [
	{
		key: 'margot',
		name: 'Margot',
		role: 'Inbound receptionist',
		kind: 'inbound',
		launch: 'passive',
		queueId: 'inbound',
		vapiId: '16357662-3b30-405f-829e-f14f18b35325',
		icon: '☎️',
		description: "Answers inbound calls from prospects and customers. Expected to route callers, answer common questions, and escalate anything she can't resolve.",
	},
];

/**
 * TEXT agents — Make scenarios (no Vapi assistant), event-driven, NOT dialers. They run off
 * pipeline events (a completed call, a new dispo), so they have no dial queue and no vapiId.
 */
export const TEXT_AGENTS: AiAgent[] = [
	{
		key: 'precall-brief',
		name: 'Pre-call brief',
		role: 'Caller prep summary before the dial',
		kind: 'text',
		launch: 'passive',
		queueId: null,
		vapiId: null,
		icon: '🗒️',
		description: "Puts together a short prep summary before a dialer calls a prospect. Expected to give the caller quick context on who they're about to talk to.",
	},
	{
		key: 'qc-triage',
		name: 'QC triage',
		role: 'Routes dispositions into the QC queue',
		kind: 'text',
		launch: 'passive',
		queueId: null,
		vapiId: null,
		icon: '🔀',
		description: 'Automatically routes call dispositions into the quality-control queue. Expected to keep QC review moving without anyone sorting calls by hand.',
	},
	{
		key: 'quality-review',
		name: 'Quality review',
		role: 'Reviews completed-call quality',
		kind: 'text',
		launch: 'passive',
		queueId: null,
		vapiId: null,
		icon: '🔍',
		description: 'Reviews completed calls for overall quality. Expected to flag issues and surface coaching opportunities for the team.',
	},
	{
		key: 'call-qa-scorer',
		name: 'Call-QA scorer',
		role: 'Scores call QA against the rubric',
		kind: 'text',
		launch: 'passive',
		queueId: null,
		vapiId: null,
		icon: '🎯',
		description: 'Scores completed calls against the QA rubric. Expected to produce a consistent, automated quality score for every call.',
	},
];

/** All roster members. Scheduler/stats iterate the dialers (voice + inbound). */
export const DIALER_AGENTS: AiAgent[] = [...VOICE_AGENTS, ...INBOUND_AGENTS];
export const AI_AGENTS: AiAgent[] = [...VOICE_AGENTS, ...INBOUND_AGENTS, ...TEXT_AGENTS];

/** Dial queues (the jobs). Replacements is planned — no agent yet. */
export const WORK_QUEUES: WorkQueue[] = [
	{ id: 'intro', name: 'Intro', defaultAgentKey: 'ivy', approvalGated: true, note: 'Cold intro — human approves before dialing.' },
	{ id: 'precall', name: 'Pre-call', defaultAgentKey: 'warren' },
	{ id: 'confirm', name: 'Confirmation', defaultAgentKey: 'connor' },
	{ id: 'sameday', name: 'Same-day', defaultAgentKey: 'sam', note: 'PHONE/ZOOM only — FACE confirms ahead of time (closer travels).' },
	{ id: 'reschedule', name: 'Reschedule', defaultAgentKey: 'renee' },
	{ id: 'postmeeting', name: 'Post-meeting', defaultAgentKey: 'parker' },
	{ id: 'inbound', name: 'Inbound', defaultAgentKey: 'margot', passive: true },
	{ id: 'replacements', name: 'Replacements', defaultAgentKey: null, planned: true, note: 'Planned — agent to be created.' },
];
